import type { ConditionalRequestDefinition } from '@/utils/dehydrateRequestData/types';
import {
  hasAmbulantNotSelected,
  hasAuslandsreisekrankenversicherungNotSelected,
  hasKrankenhaustagegeldNotSelected,
  hasKrankentagegeldNotSelected,
  hasKrankentagegeldSelectedReactive,
  hasPflegepflichtVersicherungNotSelected,
  hasPflegezusatzNotSelected,
  hasStationaerNotSelected,
  hasStationaerSelected,
  hasZahnNotSelected,
  hideBerufsstatus,
  isBeamterReactive,
  isVollversicherung,
  isZusatzversicherung,
} from '@/features/Sparten/pkv/formConfigs/sharedRenderConditions';
import { computed } from 'vue';
import { usePkvInputStore } from '@/features/Sparten/pkv/useInputStore';

const hasNoBerufsstatus: ConditionalRequestDefinition = {
  if: hideBerufsstatus,
  drop: ['berufsstatus'],
};

const isNotABeamter: ConditionalRequestDefinition = {
  if: computed(() => !isBeamterReactive.value),
  drop: ['leistungstraeger', 'beihilfesaetze'],
};

const isVollversicherungArt: ConditionalRequestDefinition = {
  if: isVollversicherung,
  drop: [
    'allgemein.altersrueckstellung',
    'ambulantOptionen.ergaenzungGKV',
    'pflegezusatzOptionen.pflegeBahrAufbauTarife',
    'pflegezusatzOptionen.pflegeBahrTarife',
    'zahnOptionen.zahnersatz',
    'zahnOptionen.zahnbehandlung',
    'zahnOptionen.kieferorthopaedie',
  ],
};

const isZusatzversicherungArt: ConditionalRequestDefinition = {
  if: isZusatzversicherung,
  drop: [
    'allgemein.selbstbeteiligungMin',
    'allgemein.selbstbeteiligungMax',
    'allgemein.pauschalleistung',
    'allgemein.beitragsrueckerstattung',
    'allgemein.osttarife',
    'allgemein.beitragsentlastungOptionen',
    'allgemein.primaerarzttarif',
    'zahnOptionen.zahnbehandlungMin',
    'zahnOptionen.zahnersatzMin',
    'zahnOptionen.kieferorthopaedieMin',
    'zahnOptionen.materialLaborKosten',
    'auslandsreisekrankenversicherung',
    'auslandsreisekrankenversicherungOptionen',
    'pflegepflichtversicherung',
    'pflegepflichtversicherungOptionen',
    'allTimeTariff.arbeitgeberanteil',
  ],
};

const hasNoAmbulantOptionen: ConditionalRequestDefinition = {
  if: hasAmbulantNotSelected,
  drop: ['ambulantOptionen'],
};

const hasNoAuslandsreisekrankenverischerungOption: ConditionalRequestDefinition =
  {
    if: hasAuslandsreisekrankenversicherungNotSelected,
    drop: ['auslandsreisekrankenversicherungOptionen'],
  };

const hasNoKrankenhaustagegeldOption: ConditionalRequestDefinition = {
  if: hasKrankenhaustagegeldNotSelected,
  drop: ['krankenhaustagegeldOptionen'],
};

const hasNoKrankentagegeldOption: ConditionalRequestDefinition = {
  if: hasKrankentagegeldNotSelected,
  drop: ['krankentagegeldOptionen'],
};

const hasOneKrankentagegeldOption: ConditionalRequestDefinition = {
  if: computed(
    () =>
      hasKrankentagegeldSelectedReactive.value &&
      usePkvInputStore().krankentagegeldOptionen.krankentageGeldFelderAnzahl
        .length === 1,
  ),
  drop: [
    'krankentagegeldOptionen.betrag1',
    'krankentagegeldOptionen.betrag2',
    'krankentagegeldOptionen.tag1',
    'krankentagegeldOptionen.tag2',
  ],
};

const hasTwoKrankentagegeldOptions: ConditionalRequestDefinition = {
  if: computed(
    () =>
      hasKrankentagegeldSelectedReactive.value &&
      usePkvInputStore().krankentagegeldOptionen.krankentageGeldFelderAnzahl
        .length === 2,
  ),
  drop: ['krankentagegeldOptionen.betrag2', 'krankentagegeldOptionen.tag2'],
};

const hasNoPflegepflichtVersicherungOption: ConditionalRequestDefinition = {
  if: hasPflegepflichtVersicherungNotSelected,
  drop: ['pflegepflichtversicherungOptionen'],
};

const hasNoPflegeZusatzOption: ConditionalRequestDefinition = {
  if: hasPflegezusatzNotSelected,
  drop: ['pflegezusatzOptionen'],
};

const hasNoStationaerOption: ConditionalRequestDefinition = {
  if: hasStationaerNotSelected,
  drop: ['stationaerOptionen'],
};

const hasStationaerOption: ConditionalRequestDefinition = {
  if: hasStationaerSelected,
  drop: [
    'stationaerOptionen.leistungVoll',
    'stationaerOptionen.leistungZusatz',
  ],
};

const hasNoZahnOption: ConditionalRequestDefinition = {
  if: hasZahnNotSelected,
  drop: ['zahnOptionen'],
};

const temporaryInputFields: ConditionalRequestDefinition = {
  if: computed(() => true),
  drop: [
    'temporaryInputFields',
    'krankentagegeldOptionen.krankentageGeldFelderAnzahl',
  ],
};

export const pkvInputDehydrationDefinitions: ConditionalRequestDefinition[] = [
  hasNoBerufsstatus,
  isNotABeamter,
  isVollversicherungArt,
  isZusatzversicherungArt,
  hasNoAmbulantOptionen,
  hasNoAuslandsreisekrankenverischerungOption,
  hasNoKrankenhaustagegeldOption,
  hasNoKrankentagegeldOption,
  hasNoPflegepflichtVersicherungOption,
  hasNoPflegeZusatzOption,
  hasNoStationaerOption,
  hasNoZahnOption,
  hasOneKrankentagegeldOption,
  hasTwoKrankentagegeldOptions,
  temporaryInputFields,
  hasStationaerOption,
];
